/**
 * Collection filters orchestrator (HopHopShop).
 *
 * The filter aside is a left-sliding drawer below 1024px and a sticky
 * inline sidebar at lg: and up. Layout flip is CSS-only (see
 * src/input.css). Owns: drawer toggle, label sync, and filter submission.
 *
 * Submit behavior: filters are applied over fetch against the Section
 * Rendering API — the page never reloads, so the visitor keeps their
 * scroll position while the grid updates under them. At lg:+, any form
 * `change` event applies immediately (no Apply button on desktop);
 * below lg:, the Apply button in the drawer footer drives it. Sort
 * changes apply at every breakpoint. If the fetch fails we fall back to
 * a plain navigation to the same URL, which is what the form would have
 * done on its own. The chips, both "Clear all" links and the empty-state
 * clear button go through the same path — they are ordinary links, so
 * they are intercepted on click (plain left-clicks only; modifier and
 * middle clicks still open a normal tab).
 *
 * Swapped on apply: the inner HTML of [data-filters] (chips, counts,
 * checked states, Apply count) and of [data-results] (grid +
 * pagination), plus the [data-filters-count] badge and the
 * [data-results-count] total in the filter bar.
 * The aside, form, overlay and <sort-selector> elements themselves stay
 * put, so drawer state and listeners bound in setup() survive.
 *
 * Delegates: <price-range-slider> stays an independent custom element
 * and re-initializes itself when the aside content is swapped. Drawer
 * state is delegated to Drawer.controllerFor() against [data-filters] +
 * [data-filter-overlay].
 */
import { Component } from "@theme/component";
import { Drawer } from "@theme/drawer";

const SIZE_PARAM = /veli(č|c)ina|size/i;

class CollectionFilters extends Component {
  #onChange;
  #request;
  #realSizesPromise;

  setup() {
    this.form = this.$("[data-filter-form]");
    this.aside = this.$("[data-filters]");
    this.overlay = this.$("[data-filter-overlay]");
    this.label = this.$("[data-filters-label]");
    this.results = this.$("[data-results]");
    this.hideSoldOut = this.dataset.hideSoldOut === "true";
    // Whether Shopify's Availability filter is switched on in Search &
    // Discovery. Without it the parameter below is inert, so adding it only
    // puts noise in the shopper's address bar.
    this.availabilityFilter = this.dataset.availabilityFilter === "true";
    this.countBadge = this.$("[data-filters-count]");
    this.resultsCount = this.$("[data-results-count]");

    this.sectionId =
      this.closest(".shopify-section")?.id.replace("shopify-section-", "") ??
      null;
    this.baseUrl = this.form?.getAttribute("action") || location.pathname;

    this.drawer = Drawer.controllerFor(this.aside, this.overlay, this);

    new MutationObserver(() => this.#updateLabel()).observe(this.aside, {
      attributes: true,
      attributeFilter: ["class"],
    });

    this.$$("[data-toggle-filters]").forEach((btn) =>
      btn.addEventListener("click", () => this.toggle()),
    );
    this.#bindCloseButtons();

    this.#updateLabel();

    this.desktopMq = window.matchMedia("(min-width: 1024px)");
    this.#onChange = (e) => {
      const size = e.target.closest?.(".hhs-size");
      if (size) this.#syncSize(size, e.target.checked);
      // Sort is a single-click choice with no Apply button of its own, so it
      // applies on every breakpoint; the filter inputs wait for Apply on mobile.
      const isSort = !!e.target.closest?.("sort-selector");
      if (!isSort && !this.desktopMq.matches) return;
      this.form?.requestSubmit();
    };
    this.form?.addEventListener("change", this.#onChange);

    this.form?.addEventListener("submit", (e) => {
      // Without a section id we can't render a partial — let the form submit.
      if (!this.sectionId || !this.results) return;
      e.preventDefault();
      this.#apply();
    });

    // Remove-a-value chips, "Clear all" and the empty-state clear button are
    // plain links; route them through the same swap so they don't navigate.
    this.addEventListener("click", (e) => {
      if (e.defaultPrevented || e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const link = e.target.closest("a[href]");
      if (!link || !this.#isFilterLink(link)) return;
      if (!this.sectionId || !this.results) return;
      e.preventDefault();
      this.drawer.close();
      this.#render(
        this.#withoutStaleAvailability(link.getAttribute("href")),
        true,
      );
    });

    window.addEventListener("popstate", () => {
      if (!this.sectionId || location.pathname !== this.baseUrl) return;
      this.#render(location.pathname + location.search, false);
    });

    this.#pruneSizes();
  }

  toggle() {
    this.drawer.isOpen ? this.drawer.close() : this.drawer.open();
  }

  /**
   * A size pill's selected look is server-rendered (is-active, no grey inline
   * border) and so only appears once the filter is applied. On mobile that
   * waits for the Apply button, so mirror the markup for an applied value
   * here — otherwise a tapped size looks exactly like an untapped one.
   */
  #syncSize(label, on) {
    label.classList.toggle("is-active", on);
    label.classList.toggle("bg-pink-pale", on);
    label.classList.toggle("-translate-y-0.5", on);
    label.classList.toggle("hover:border-hophop-pink", !on);
    label.style.borderColor = on ? "" : "#c0c0c0";
  }

  /**
   * url_to_remove links from Shopify keep every other param, including the
   * availability rider #urlFromForm adds alongside sizes. When the last size
   * is being removed, drop the rider too so it can't keep filtering the grid
   * invisibly.
   */
  #withoutStaleAvailability(href) {
    const url = new URL(href, location.origin);
    const params = url.searchParams;
    // When the section hides sold-out products outright the rider is not
    // stale, it is the point — leave it wherever it appears.
    if (this.hideSoldOut) return url.pathname + url.search;
    if (params.has("filter.v.availability")) {
      let hasSize = false;
      for (const key of params.keys()) {
        if (key.startsWith("filter.v.option.") && SIZE_PARAM.test(key)) {
          hasSize = true;
          break;
        }
      }
      if (!hasSize) params.delete("filter.v.availability");
    }
    return url.pathname + url.search;
  }

  /** Apply the current form state: fetch, swap, push the URL. */
  #apply() {
    this.drawer.close();
    this.#render(this.#urlFromForm(), true);
  }

  /**
   * Collection URL for the current form state. Empty values are dropped so
   * an untouched price slider doesn't end up in the query, and `page` is
   * absent by design — a filter change sends you back to page 1.
   *
   * Picking a size quietly adds `filter.v.availability=1`: Shopify matches
   * option + availability on the same variant, so "37" then means "37 in
   * stock" instead of "any product that ever had a 37". The availability
   * filter itself is never rendered — it only rides along here. (Requires
   * the Availability filter to be enabled in Search & Discovery.)
   */
  #urlFromForm() {
    const params = new URLSearchParams();
    let hasSizeFilter = false;
    for (const [key, value] of new FormData(this.form).entries()) {
      if (typeof value !== "string" || value === "") continue;
      params.append(key, value);
      if (key.startsWith("filter.v.option.") && SIZE_PARAM.test(key)) {
        hasSizeFilter = true;
      }
    }
    if (
      this.availabilityFilter &&
      (this.hideSoldOut || hasSizeFilter) &&
      !params.has("filter.v.availability")
    ) {
      params.append("filter.v.availability", "1");
    }
    const query = params.toString();
    return query ? `${this.baseUrl}?${query}` : this.baseUrl;
  }

  async #render(url, push) {
    this.#request?.abort();
    const request = new AbortController();
    this.#request = request;
    this.setAttribute("aria-busy", "true");

    const separator = url.includes("?") ? "&" : "?";
    const sectionUrl = `${url}${separator}section_id=${encodeURIComponent(this.sectionId)}`;

    try {
      const response = await fetch(sectionUrl, { signal: request.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const doc = new DOMParser().parseFromString(
        await response.text(),
        "text/html",
      );
      // A password page, a challenge or an app interstitial answers 200 with
      // markup that has no results in it — treat that as a failed render
      // rather than silently leaving the old grid on screen.
      if (!this.#swap(doc)) throw new Error("No results in response");
      if (push) history.pushState({ hhsFilters: true }, "", url);
      // The swap re-rendered the full size list — hide the dead sizes again.
      this.#pruneSizes();
    } catch (error) {
      if (error.name === "AbortError") return;
      // Network or section-render failure: do what the plain form would do.
      location.assign(url);
      return;
    } finally {
      // A newer apply may already be in flight — it owns the busy state now.
      if (this.#request === request) {
        this.#request = null;
        this.removeAttribute("aria-busy");
      }
    }
  }

  /**
   * Hide size values with no in-stock variant anywhere in the collection.
   * Liquid can't compute this (filter counts ignore stock, and
   * collection.products caps at 50), so the collection's own products.json
   * is swept once per page load and the sizes with a purchasable variant
   * are collected; pills outside that set are hidden. Checked pills always
   * stay so they can be unchecked.
   */
  async #pruneSizes() {
    // Search results have no products.json to sweep.
    if (!this.aside || this.dataset.pruneSizes === "false") return;
    const alive = await this.#realSizes();
    if (!alive || alive.size === 0) return;
    this.aside
      .querySelectorAll('input[name^="filter.v.option."]')
      .forEach((input) => {
        if (!SIZE_PARAM.test(input.name)) return;
        const label = input.closest("label");
        if (!label) return;
        const dead = !input.checked && !alive.has(input.value.trim());
        // style.display, not [hidden]: the pill's display class outranks
        // the UA rule for the hidden attribute.
        label.style.display = dead ? "none" : "";
      });
  }

  /**
   * The set of size values with at least one available variant in this
   * collection, from /products.json (250 per page, up to 8 pages). Memoized:
   * the answer is collection-wide, so filter changes don't re-fetch. Returns
   * null — prune nothing — when the sweep can't be trusted: a fetch failed,
   * or the collection is bigger than the sweep and a real size might live in
   * the unseen tail.
   */
  async #realSizes() {
    this.#realSizesPromise ??= (async () => {
      try {
        const sizes = new Set();
        for (let page = 1; page <= 8; page++) {
          const response = await fetch(
            `${this.baseUrl}/products.json?limit=250&page=${page}`,
          );
          if (!response.ok) return null;
          const { products } = await response.json();
          for (const product of products ?? []) {
            const options = product.options ?? [];
            const sizeOption = options.find((option) =>
              SIZE_PARAM.test(
                typeof option === "string" ? option : (option?.name ?? ""),
              ),
            );
            if (!sizeOption) continue;
            const position =
              typeof sizeOption === "string"
                ? options.indexOf(sizeOption) + 1
                : (sizeOption.position ?? options.indexOf(sizeOption) + 1);
            for (const variant of product.variants ?? []) {
              const value = variant[`option${position}`];
              if (variant.available && value != null) {
                sizes.add(String(value).trim());
              }
            }
          }
          if (!products || products.length < 250) return sizes;
        }
        return null;
      } catch {
        return null;
      }
    })();
    return this.#realSizesPromise;
  }

  /** @returns {boolean} whether the response actually carried a new grid. */
  #swap(doc) {
    const aside = doc.querySelector("[data-filters]");
    const results = doc.querySelector("[data-results]");
    const badge = doc.querySelector("[data-filters-count]");
    const resultsCount = doc.querySelector("[data-results-count]");
    if (!results) return false;

    if (aside) {
      const openState = this.#groupState();
      this.aside.innerHTML = aside.innerHTML;
      this.#restoreGroupState(openState);
      this.#bindCloseButtons();
    }
    this.results.innerHTML = results.innerHTML;
    if (badge && this.countBadge)
      this.countBadge.textContent = badge.textContent;
    if (resultsCount && this.resultsCount)
      this.resultsCount.textContent = resultsCount.textContent;

    // "Clear all" drops sort_by along with the filters, so the selector has to
    // follow whatever sort the server actually rendered.
    const nextSort = doc.querySelector(
      "sort-selector [data-sort-input]",
    )?.value;
    const sortSelector = this.$("sort-selector");
    if (nextSort && typeof sortSelector?.sync === "function")
      sortSelector.sync(nextSort);

    return true;
  }

  /**
   * Links that change the filter state: everything inside the aside (the
   * removable chips and both "Clear all" links) plus the empty-state button.
   * Product and pagination links are left to navigate normally.
   */
  #isFilterLink(link) {
    return this.aside.contains(link) || link.hasAttribute("data-clear-filters");
  }

  /**
   * Which filter groups the visitor has open, keyed by label — the swap would
   * otherwise snap every <details> back to its server-rendered default.
   */
  #groupState() {
    const state = new Map();
    this.aside.querySelectorAll("details").forEach((group) => {
      const label = group.querySelector("summary span")?.textContent.trim();
      if (label) state.set(label, group.open);
    });
    return state;
  }

  /** Groups the server sent that weren't on screen before keep their default. */
  #restoreGroupState(state) {
    this.aside.querySelectorAll("details").forEach((group) => {
      const label = group.querySelector("summary span")?.textContent.trim();
      if (label && state.has(label)) group.open = state.get(label);
    });
  }

  /** (Re)bind the drawer close buttons — they live inside the swapped aside. */
  #bindCloseButtons() {
    this.$$("[data-close-filters]").forEach((btn) => {
      if (btn.dataset.closeBound === "1") return;
      btn.dataset.closeBound = "1";
      btn.addEventListener("click", () => this.drawer.close());
    });
  }

  #updateLabel() {
    if (!this.label) return;
    this.label.textContent = this.drawer.isOpen ? "Hide filters" : "Filter";
  }
}

customElements.define("collection-filters", CollectionFilters);

export { CollectionFilters };
